import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import BreakingNews from '../components/home/BreakingNews';
import TrumpTruthFeed from '../components/feed/TrumpTruthFeed';
import {useStats} from '../hooks/useStats';
import * as FiIcons from 'react-icons/fi';

const {FiBookOpen, FiCalendar, FiUsers, FiTrendingUp, FiAward, FiPlay, FiShield, FiStar, FiCompass, FiMic, FiMessageSquare, FiFlag, FiHeart, FiGlobe, FiBook, FiTarget} = FiIcons;

const Home = () => {
  // State to track which news item is active to sync with Trump's feed
  const [activeNewsIndex, setActiveNewsIndex] = useState(0);

  // Get stats from our custom hook
  const {stats} = useStats();

  const mainFeatures = [
    {
      icon: FiBookOpen,
      title: 'Constitutional Learning',
      description: 'Master the Constitution, Bill of Rights, and founding principles that built our great nation.',
      link: '/learning',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=250&fit=crop',
      color: 'bg-blue-100 text-blue-800',
      buttonColor: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      icon: FiUsers,
      title: 'Community Groups',
      description: 'Connect with like-minded conservatives in your area through local and national interest groups.',
      link: '/community',
      image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=400&h=250&fit=crop',
      color: 'bg-purple-100 text-purple-800',
      buttonColor: 'bg-purple-600 hover:bg-purple-700'
    },
    {
      icon: FiCalendar,
      title: 'Republican Events',
      description: 'Join rallies, town halls, and conservative gatherings across America. Get involved locally.',
      link: '/events',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=250&fit=crop',
      color: 'bg-red-100 text-red-800',
      buttonColor: 'bg-red-600 hover:bg-red-700'
    },
    {
      icon: FiTarget,
      title: 'GOP Candidates',
      description: 'Support Republican candidates fighting for conservative values at every level of government.',
      link: '/candidates',
      image: 'https://cdn.abcotvs.com/dip/images/15075520_072024-kgo-ap-donald-trump-jd-vance-1st-campaign-rally-img.jpg',
      color: 'bg-green-100 text-green-800',
      buttonColor: 'bg-green-600 hover:bg-green-700'
    }
  ];

  const secondaryFeatures = [
    {
      icon: FiCompass,
      title: 'Find Representatives',
      description: 'Connect with your elected officials and make your voice heard on important issues.',
      link: '/representatives'
    },
    {
      icon: FiMic,
      title: 'Conservative Resources',
      description: 'Access trusted conservative news, podcasts, books, and media sources.',
      link: '/resources'
    },
    {
      icon: FiMessageSquare,
      title: 'Mentorship Program',
      description: 'Learn from experienced conservatives through our mentorship network.',
      link: '/community'
    },
    {
      icon: FiFlag,
      title: 'Volunteer Opportunities',
      description: 'Find ways to get involved in campaigns and conservative causes in your area.',
      link: '/events'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Young Republican Leader',
      location: 'Texas',
      text: 'This platform strengthened my understanding of constitutional principles and connected me with fellow Republicans nationwide!',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Colonel Mike R.',
      role: 'Military Veteran & GOP Volunteer',
      location: 'Florida',
      text: 'Finally, a place where patriots can unite under Republican values and defend our constitutional freedoms.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Jennifer L.',
      role: 'Conservative Business Owner',
      location: 'Arizona',
      text: 'The Republican network here helped grow my business while supporting America First principles.',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
    }
  ];

  const principles = [
    {
      icon: FiShield,
      title: 'Constitutional Rights',
      description: 'Defending the Constitution and Bill of Rights'
    },
    {
      icon: FiStar,
      title: 'Limited Government',
      description: 'Promoting fiscal responsibility and smaller government'
    },
    {
      icon: FiTrendingUp,
      title: 'Free Markets',
      description: 'Supporting capitalism and economic freedom'
    },
    {
      icon: FiFlag,
      title: 'Traditional Values',
      description: 'Preserving family values and American traditions'
    }
  ];

  return (
    <div className="min-h-screen" id="main-content">
      {/* Hero Section with Patriotic Background */}
      <section className="relative overflow-hidden bg-gradient-to-br from-patriot-navy via-patriot-red to-patriot-navy">
        {/* Patriotic Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M30 30l15-15v30l-15-15zm-15 0l15 15v-30l-15 15z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center text-white"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 font-display">
              Defend. <span className="text-patriot-gold">Unite.</span> Lead.
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100 max-w-4xl mx-auto leading-relaxed">
              The premier platform for <span className="font-bold text-patriot-gold">Republican Patriots</span> to learn, connect, and grow together. Join <span className="font-bold text-patriot-gold">{stats.patriots}</span> conservatives united under the Republican banner.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/signup"
                className="bg-patriot-gold text-patriot-navy px-8 py-4 rounded-md font-semibold text-lg hover:bg-patriot-gold-light transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center space-x-2">
                  <span>Join the GOP Movement</span>
                  <SafeIcon icon={FiShield} className="h-5 w-5" />
                </span>
              </Link>
              <Link
                to="/learning"
                className="border-2 border-patriot-gold text-patriot-gold px-8 py-4 rounded-md font-semibold text-lg hover:bg-patriot-gold hover:text-patriot-navy transition-all duration-300"
              >
                <span className="flex items-center space-x-2">
                  <SafeIcon icon={FiPlay} className="h-5 w-5" />
                  <span>Learn the Constitution</span>
                </span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Breaking News Section */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-patriot-navy text-center mb-4">
              Breaking News
            </h2>
            <p className="text-gray-600 text-center max-w-3xl mx-auto">
              Stay informed with the latest developments on conservative issues and policies
            </p>
          </div>
          {/* Breaking News Component */}
          <BreakingNews />
        </div>
      </section>

      {/* Information Hub Section - Updated with Image Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-patriot-navy mb-4 font-display">
              Your Information Hub
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find everything you need to stay informed about the Republican movement
            </p>
            <div className="h-1 w-24 bg-patriot-red rounded-full mx-auto mt-4"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="group"
              >
                <Link to={feature.link} className="block">
                  <div className="relative h-48 mb-4 rounded-lg overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-70"></div>
                    <div className="absolute bottom-0 left-0 p-4 w-full">
                      <div className="flex items-center">
                        <div className="bg-white p-2 rounded-full mr-3">
                          <SafeIcon icon={feature.icon} className="h-5 w-5 text-patriot-red" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                  <div className="mt-3 flex items-center text-patriot-red font-medium">
                    Learn More
                    <SafeIcon icon={FiTrendingUp} className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved Today Section - Updated with Alternating Layout and Full Rectangle Clickability */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-patriot-navy mb-4 font-display">
              Get Involved Today
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Multiple ways to participate in the Republican movement
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {secondaryFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{opacity: 0, x: index % 2 === 0 ? -30 : 30}}
                animate={{opacity: 1, x: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className={`bg-white p-8 rounded-lg shadow-lg border-t-4 hover:shadow-xl transition-all duration-300 ${
                  index % 4 === 0 ? 'border-patriot-red' : 
                  index % 4 === 1 ? 'border-patriot-navy' : 
                  index % 4 === 2 ? 'border-patriot-gold' : 'border-green-600'
                }`}
              >
                <Link to={feature.link} className="block h-full">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-xl ${
                      index % 4 === 0 ? 'bg-patriot-red bg-opacity-10' : 
                      index % 4 === 1 ? 'bg-patriot-navy bg-opacity-10' : 
                      index % 4 === 2 ? 'bg-patriot-gold bg-opacity-10' : 'bg-green-100'
                    }`}>
                      <SafeIcon icon={feature.icon} className={`h-7 w-7 ${
                        index % 4 === 0 ? 'text-patriot-red' : 
                        index % 4 === 1 ? 'text-patriot-navy' : 
                        index % 4 === 2 ? 'text-patriot-gold' : 'text-green-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-patriot-navy mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {feature.description}
                      </p>
                      <div className={`inline-flex items-center font-medium hover:underline transition-colors duration-200 ${
                        index % 4 === 0 ? 'text-patriot-red hover:text-patriot-red-dark' : 
                        index % 4 === 1 ? 'text-patriot-navy hover:text-patriot-navy-dark' : 
                        index % 4 === 2 ? 'text-patriot-gold hover:text-patriot-gold-dark' : 'text-green-600 hover:text-green-700'
                      }`}>
                        Learn More
                        <SafeIcon icon={FiTrendingUp} className="ml-1 h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section with Patriotic Design - Moved Up */}
      <section className="py-16 bg-patriot-navy relative overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M20 20l10-10v20l-10-10zm-10 0l10 10v-20l-10 10z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '40px 40px'
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-display">
              Growing the Republican Movement
            </h2>
            <div className="flex justify-center">
              <div className="h-1 w-24 bg-patriot-gold rounded-full"></div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.1}}
              className="text-center bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="text-3xl md:text-4xl font-bold text-patriot-gold mb-2 font-display">
                {stats.patriots}
              </div>
              <div className="text-white font-medium">Republican Patriots</div>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="text-center bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="text-3xl md:text-4xl font-bold text-patriot-gold mb-2 font-display">
                {stats.lessons}
              </div>
              <div className="text-white font-medium">Constitutional Lessons</div>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
              className="text-center bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="text-3xl md:text-4xl font-bold text-patriot-gold mb-2 font-display">
                {stats.events}
              </div>
              <div className="text-white font-medium">GOP Events Monthly</div>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              className="text-center bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="text-3xl md:text-4xl font-bold text-patriot-gold mb-2 font-display">
                {stats.candidates}
              </div>
              <div className="text-white font-medium">Republican Candidates</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Republican Principles Section */}
      <section className="py-16 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-r from-red-50 to-blue-50 opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-patriot-navy mb-4 font-display">
              Republican Core Principles
            </h2>
            <p className="text-xl text-gray-600">The values that guide our party and our nation</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="text-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-patriot-red bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={principle.icon} className="h-8 w-8 text-patriot-red" />
                </div>
                <h3 className="text-lg font-semibold text-patriot-navy mb-2">{principle.title}</h3>
                <p className="text-gray-600 text-sm">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Updated with a distinct background color */}
      <section className="py-20 bg-patriot-red bg-opacity-5 relative">
        <div className="absolute inset-0 bg-patriot-navy opacity-5 pattern-stars"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-patriot-navy mb-4 font-display">
              What Republican Patriots Are Saying
            </h2>
            <p className="text-xl text-gray-600">Real stories from conservatives defending American values</p>
            <div className="h-1 w-24 bg-patriot-red rounded-full mx-auto mt-4"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-white rounded-lg p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={`${testimonial.name}'s avatar`}
                    className="w-12 h-12 rounded-full border-2 border-patriot-gold mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-patriot-navy">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-xs text-patriot-red font-medium">{testimonial.location}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic leading-relaxed mb-4">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex text-patriot-gold">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">Verified Republican</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trump Truth Social Feed Section - Moved to Bottom */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-patriot-navy mb-2">
              President Trump's Latest Updates
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Stay connected with President Trump through his Truth Social posts
            </p>
          </div>
          {/* Trump Truth Social Feed */}
          <TrumpTruthFeed relatedNewsIndex={activeNewsIndex} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-patriot-red to-patriot-navy relative overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M30 30l15-15v30l-15-15zm-15 0l15 15v-30l-15 15z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-white"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">
              Ready to Defend the Constitution?
            </h2>
            <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Join fellow Republican patriots in defending constitutional values, supporting conservative candidates, and building a stronger America. The future of our republic depends on patriots like you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/signup"
                className="bg-patriot-gold text-patriot-navy px-8 py-4 rounded-md font-semibold text-lg hover:bg-patriot-gold-light transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center space-x-2">
                  <span>Join the Republican Movement</span>
                  <SafeIcon icon={FiShield} className="h-5 w-5" />
                </span>
              </Link>
              <div className="text-patriot-gold font-medium">
                Join {stats.patriots} Republican patriots already defending America!
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;